import { Clock, Effect } from 'effect';

import { POSTGRES_MIGRATE_STATEMENTS } from './schema.js';
import {
  defineEffectDocumentPersistence,
  isEffectSqlRunner,
  makeDocumentPersistenceError,
  BlobKind,
  BlobRecord,
  DefinedEffectDocumentPersistence,
  DocumentPersistenceError,
  DocumentPersistenceOperation,
  DocumentRecord,
  LeaseRecord,
  PersistenceOptions,
  SqlRunner,
  SqlTx,
} from './types.js';

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function parseJson(value: unknown): Record<string, unknown> {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Uint8Array)
  ) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    const parsed: unknown = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  }
  return {};
}

function asBoolean(value: unknown): boolean {
  return value === true || value === 't' || value === 'true' || value === 1 || value === '1';
}

function asBytes(value: unknown): Uint8Array {
  if (value instanceof Uint8Array) return new Uint8Array(value);
  if (typeof Buffer !== 'undefined' && Buffer.isBuffer(value)) {
    return new Uint8Array(value);
  }
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (typeof value === 'string') {
    // pg may return hex for bytea (`\\x…`) depending on driver config.
    if (value.startsWith('\\x')) {
      const hex = value.slice(2);
      const out = new Uint8Array(hex.length / 2);
      for (let i = 0; i < out.length; i += 1) {
        out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
      }
      return out;
    }
    return new TextEncoder().encode(value);
  }
  throw new Error('okra_blobs.bytes is not bytea');
}

function mapDocument(row: Record<string, unknown>): DocumentRecord {
  return {
    id: String(row.id),
    ownerUserId: String(row.owner_user_id),
    orgId: row.org_id == null ? null : String(row.org_id),
    projectId: row.project_id == null ? null : String(row.project_id),
    isPublished: asBoolean(row.is_published),
    spec: parseJson(row.spec),
    meta: parseJson(row.meta),
    graph: parseJson(row.graph),
    phase: String(row.phase ?? 'idle'),
    createdAt: new Date(String(row.created_at)).toISOString(),
    updatedAt: new Date(String(row.updated_at)).toISOString(),
  };
}

const DOCUMENT_COLUMNS = `id, owner_user_id, org_id, project_id, is_published, spec, meta, graph, phase, created_at, updated_at`;

const UPSERT_DOCUMENT = `
INSERT INTO okra_documents (
  id, owner_user_id, org_id, project_id, is_published, spec, meta, graph, phase, created_at, updated_at
) VALUES (
  $1, $2, $3, $4, $5, $6::jsonb, $7::jsonb, $8::jsonb, $9, $10::timestamptz, $11::timestamptz
)
ON CONFLICT (id) DO UPDATE SET
  owner_user_id = EXCLUDED.owner_user_id,
  org_id = EXCLUDED.org_id,
  project_id = EXCLUDED.project_id,
  is_published = EXCLUDED.is_published,
  spec = EXCLUDED.spec,
  meta = EXCLUDED.meta,
  graph = EXCLUDED.graph,
  phase = EXCLUDED.phase,
  updated_at = EXCLUDED.updated_at
RETURNING ${DOCUMENT_COLUMNS}
`;

const SELECT_DOCUMENT = `SELECT ${DOCUMENT_COLUMNS} FROM okra_documents WHERE id = $1`;
const LIST_BY_OWNER = `SELECT ${DOCUMENT_COLUMNS} FROM okra_documents WHERE owner_user_id = $1 ORDER BY updated_at DESC`;

const UPSERT_BLOB = `
INSERT INTO okra_blobs (document_id, kind, name, content_type, bytes)
VALUES ($1, $2, $3, $4, $5)
ON CONFLICT (document_id, kind, name) DO UPDATE SET
  content_type = EXCLUDED.content_type,
  bytes = EXCLUDED.bytes
`;

const SELECT_BLOB = `
SELECT document_id, kind, name, content_type, bytes
FROM okra_blobs
WHERE document_id = $1 AND kind = $2 AND name = $3
`;

const ACQUIRE_LEASE = `
INSERT INTO okra_leases (document_id, instance_id, expires_at)
VALUES ($1, $2, $3::timestamptz)
ON CONFLICT (document_id) DO UPDATE SET
  instance_id = EXCLUDED.instance_id,
  expires_at = EXCLUDED.expires_at
WHERE okra_leases.instance_id = EXCLUDED.instance_id
   OR okra_leases.expires_at <= $4::timestamptz
RETURNING instance_id, expires_at
`;

const SELECT_LEASE = `SELECT document_id, instance_id, expires_at FROM okra_leases WHERE document_id = $1`;
const DELETE_LEASE = `DELETE FROM okra_leases WHERE document_id = $1 AND instance_id = $2`;

function mapLease(row: Record<string, unknown>): LeaseRecord {
  return {
    documentId: String(row.document_id),
    instanceId: String(row.instance_id),
    expiresAt: new Date(String(row.expires_at)).toISOString(),
  };
}

function mapBlob(row: Record<string, unknown>): BlobRecord {
  const kind = row.kind === 'media' ? 'media' : 'source';
  return {
    documentId: String(row.document_id),
    kind,
    name: String(row.name),
    contentType: String(row.content_type),
    bytes: asBytes(row.bytes),
  };
}

function attemptSync<A>(
  operation: DocumentPersistenceOperation,
  evaluate: () => A,
): Effect.Effect<A, DocumentPersistenceError> {
  return Effect.try({
    try: evaluate,
    catch: (cause) => makeDocumentPersistenceError(operation, cause),
  });
}

function attemptPromise<A>(
  operation: DocumentPersistenceOperation,
  evaluate: () => Promise<A>,
): Effect.Effect<A, DocumentPersistenceError> {
  return Effect.tryPromise({
    try: evaluate,
    catch: (cause) => makeDocumentPersistenceError(operation, cause),
  });
}

export function createPostgresPersistence(
  runner: SqlRunner,
  options: PersistenceOptions = {},
): DefinedEffectDocumentPersistence {
  const now = (
    operation: DocumentPersistenceOperation,
  ): Effect.Effect<number, DocumentPersistenceError> =>
    options.clock ? attemptSync(operation, () => options.clock!.now()) : Clock.currentTimeMillis;

  const queryEffect = <T>(
    operation: DocumentPersistenceOperation,
    sql: SqlTx | SqlRunner,
    text: string,
    params?: unknown[],
  ): Effect.Effect<T[], DocumentPersistenceError> => {
    if (sql === runner && isEffectSqlRunner(runner)) {
      return runner
        .queryEffect<T>(text, params)
        .pipe(Effect.mapError((error) => makeDocumentPersistenceError(operation, error.cause)));
    }
    return attemptPromise(operation, () => sql.query<T>(text, params));
  };

  return defineEffectDocumentPersistence({
    migrateEffect: () =>
      Effect.forEach(
        POSTGRES_MIGRATE_STATEMENTS,
        (statement) => queryEffect('migrate', runner, statement),
        { concurrency: 1, discard: true },
      ),
    closeEffect: () =>
      isEffectSqlRunner(runner)
        ? runner
            .closeEffect()
            .pipe(Effect.mapError((error) => makeDocumentPersistenceError('close', error.cause)))
        : attemptPromise('close', () => runner.close()),
    putDocumentEffect: (input) =>
      Effect.gen(function* () {
        const nowMs = yield* now('put-document');
        const currentTime = yield* attemptSync('put-document', () => iso(nowMs));
        const createdAt = input.createdAt ?? currentTime;
        const updatedAt = input.updatedAt ?? currentTime;
        const rows = yield* queryEffect<Record<string, unknown>>(
          'put-document',
          runner,
          UPSERT_DOCUMENT,
          [
            input.id,
            input.ownerUserId,
            input.orgId,
            input.projectId,
            input.isPublished,
            JSON.stringify(input.spec),
            JSON.stringify(input.meta),
            JSON.stringify(input.graph),
            input.phase,
            createdAt,
            updatedAt,
          ],
        );
        return yield* attemptSync('put-document', () => {
          const row = rows[0];
          if (!row) {
            throw new Error(`failed to upsert document ${input.id}`);
          }
          return mapDocument(row);
        });
      }),
    getDocumentEffect: (id) =>
      Effect.gen(function* () {
        const rows = yield* queryEffect<Record<string, unknown>>(
          'get-document',
          runner,
          SELECT_DOCUMENT,
          [id],
        );
        return yield* attemptSync('get-document', () => {
          const row = rows[0];
          return row ? mapDocument(row) : null;
        });
      }),
    listDocumentsByOwnerEffect: (ownerUserId) =>
      Effect.gen(function* () {
        const rows = yield* queryEffect<Record<string, unknown>>(
          'list-documents-by-owner',
          runner,
          LIST_BY_OWNER,
          [ownerUserId],
        );
        return yield* attemptSync('list-documents-by-owner', () => rows.map(mapDocument));
      }),
    putBlobEffect: (blob) =>
      Effect.gen(function* () {
        const existing = yield* queryEffect<Record<string, unknown>>(
          'put-blob',
          runner,
          SELECT_DOCUMENT,
          [blob.documentId],
        );
        if (!existing[0]) {
          const cause = new Error(`document ${blob.documentId} does not exist`);
          return yield* makeDocumentPersistenceError('put-blob', cause);
        }
        yield* queryEffect('put-blob', runner, UPSERT_BLOB, [
          blob.documentId,
          blob.kind,
          blob.name,
          blob.contentType,
          blob.bytes,
        ]);
      }),
    getBlobEffect: (documentId, kind: BlobKind, name) =>
      Effect.gen(function* () {
        const rows = yield* queryEffect<Record<string, unknown>>('get-blob', runner, SELECT_BLOB, [
          documentId,
          kind,
          name,
        ]);
        return yield* attemptSync('get-blob', () => {
          const row = rows[0];
          return row ? mapBlob(row) : null;
        });
      }),
    acquireLeaseEffect: (documentId, instanceId, ttlMs) =>
      Effect.gen(function* () {
        const existing = yield* queryEffect<Record<string, unknown>>(
          'acquire-lease',
          runner,
          SELECT_DOCUMENT,
          [documentId],
        );
        if (!existing[0]) {
          const cause = new Error(`document ${documentId} does not exist`);
          return yield* makeDocumentPersistenceError('acquire-lease', cause);
        }
        const nowMs = yield* now('acquire-lease');
        const [expiresAt, currentTime] = yield* attemptSync(
          'acquire-lease',
          () => [iso(nowMs + ttlMs), iso(nowMs)] as const,
        );
        const rows = yield* queryEffect<Record<string, unknown>>(
          'acquire-lease',
          runner,
          ACQUIRE_LEASE,
          [documentId, instanceId, expiresAt, currentTime],
        );
        return yield* attemptSync('acquire-lease', () => {
          const row = rows[0];
          return Boolean(row) && String(row!.instance_id) === instanceId;
        });
      }),
    releaseLeaseEffect: (documentId, instanceId) =>
      queryEffect('release-lease', runner, DELETE_LEASE, [documentId, instanceId]).pipe(
        Effect.asVoid,
      ),
    getLeaseEffect: (documentId) =>
      Effect.gen(function* () {
        const rows = yield* queryEffect<Record<string, unknown>>(
          'get-lease',
          runner,
          SELECT_LEASE,
          [documentId],
        );
        const row = rows[0];
        if (!row) return null;
        const lease = yield* attemptSync('get-lease', () => mapLease(row));
        const nowMs = yield* now('get-lease');
        return Date.parse(lease.expiresAt) <= nowMs ? null : lease;
      }),
  });
}
